/**
 * Site Organizer App Component
 *
 * Central hub for organizing study portals, team members, and contacts.
 * Integrated into SiteOperations.io - uses shared authentication.
 * Note: StudyOrganizerProvider is now in App.tsx to persist state across navigation.
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { StudyList, StudyDetail, AddStudy, OrgLinks } from './pages';
import { Layout } from './components';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Protected route wrapper - uses shared auth from main app
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, user: authUser, currentSite, sitesLoading } = useAuth();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Check entitlement when component mounts or site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (authUser?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const studyOrganizerTool = tools.find((t: any) => t.tool_id === 'study_organizer');
        setHasEntitlement(studyOrganizerTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking Site Organizer entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    if (session && !sitesLoading) {
      checkEntitlement();
    }
  }, [session, authUser?.role, currentSite, sitesLoading]);

  if (sitesLoading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // If no session, redirect to main login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Check entitlement (super admins bypass this check)
  if (entitlementChecked && !hasEntitlement && authUser?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            You do not have access to Site Organizer for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Site Organizer Routes
function SiteOrganizerRoutes() {
  return (
    <Routes>
      {/* Default route goes to study list */}
      <Route path="/" element={<Navigate to="/siteorganizer/studies" replace />} />

      {/* Study list */}
      <Route
        path="/studies"
        element={
          <ProtectedRoute>
            <Layout>
              <StudyList />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Study detail */}
      <Route
        path="/study/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <StudyDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Add study */}
      <Route
        path="/add"
        element={
          <ProtectedRoute>
            <Layout>
              <AddStudy />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Org links management */}
      <Route
        path="/org-links"
        element={
          <ProtectedRoute>
            <Layout>
              <OrgLinks />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to studies */}
      <Route path="*" element={<Navigate to="/siteorganizer/studies" replace />} />
    </Routes>
  );
}

// Main Site Organizer App
// Note: Provider is now in App.tsx to persist state across navigation
export function StudyOrganizerApp() {
  return <SiteOrganizerRoutes />;
}

export default StudyOrganizerApp;
